import { debounce } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';

import { analytics } from 'analytics';
import { useUpstreamFormContext } from 'contexts/upstreamFormContext';
import JSInputParser from 'diagram/jsInputParser';
import { Sankey } from 'generated-api';

import { useSankeyFormContext } from '../contexts/sankeyFormContext';
import { getChartFromSankeyFormValues } from './chart';
import {
  convertToMaticInput,
  emptySankey,
  sankeyFormFlowsArrayToMap,
  sankeyFormFlowsMapToArray,
  sankeyFormNodesArrayToMap,
  sankeyFormNodesMapToArray,
} from './sankey';

/*
 * Returns true if the form has been modified and has any dirty fields.
 * NOTE: Checking formState.isDirty is unreliable across form resets!
 */
export const hasModifiedForm = (form: UseFormReturn<any>) =>
  Object.keys(form.formState.dirtyFields).length > 0;

/*
 * Returns the current Sankey's ID from the form context.
 */
export const useCurrentFormSankeyId = (): number | undefined => {
  const { form } = useSankeyFormContext();
  return form?.getValues('id');
};

/*
 * Updates the chart field in the Sankey form with the current chart JSON
 * representation based on the current form values.
 * This is useful for keeping the chart field in sync with the form data.
 */
export const updateChart = (form: SankeyForm) => {
  form.setValue('chart', getChartFromSankeyFormValues(form.getValues()));
};

/*
 * Returns the SVG representation of the Sankey diagram without the font <defs> tag.
 */
const getBareSVGRepresentation = () => {
  const representation = document.getElementById('sankey_svg')?.outerHTML ?? '';
  return representation.replace(/<defs>[\s\S]*?<\/defs>/g, '');
};

/*
 * Converts Sankey form data into the format expected by save / create Sankey API.
 */
export const formToSankey = (form: SankeyForm): WithExplicitFields<Sankey> => ({
  /* Copied values */
  background_color: form.getValues('background_color'),
  canvas_height: form.getValues('canvas_height'),
  canvas_size: form.getValues('canvas_size'),
  canvas_width: form.getValues('canvas_width'),
  chart_type_list_position: form.getValues('chart_type_list_position'),
  chart_type_name: form.getValues('chart_type_name'),
  created_at: form.getValues('created_at'),
  created_with: form.getValues('created_with'),
  company_name: form.getValues('company_name'),
  comparison_line_prefix: form.getValues('comparison_line_prefix'),
  comparison_line_suffix: form.getValues('comparison_line_suffix'),
  comparison_line_value_type: form.getValues('comparison_line_value_type'),
  custom_title: form.getValues('custom_title'),
  data_origin: form.getValues('data_origin'),
  data_source_notes: form.getValues('data_source_notes'),
  download_image_format: form.getValues('download_image_format'),
  download_scale_png: form.getValues('download_scale_png'),
  fiscal_year: form.getValues('fiscal_year'),
  font_family: form.getValues('font_family'),
  font_size: form.getValues('font_size'),
  id: form.getValues('id'),
  is_listed_chart_type_in_footer: form.getValues(
    'is_listed_chart_type_in_footer'
  ),
  is_main_example: form.getValues('is_main_example'),
  is_official: form.getValues('is_official'),
  is_template: form.getValues('is_template'),
  label_pos: form.getValues('label_pos'),
  node_height: form.getValues('node_height'),
  node_spacing: form.getValues('node_spacing'),
  node_width: form.getValues('node_width'),
  nr_of_decimals: form.getValues('nr_of_decimals'),
  owner_id: form.getValues('owner_id'),
  points: form.getValues('points'),
  public_permission: form.getValues('public_permission'),
  remembered_node_moves: form.getValues('remembered_node_moves'),
  reporting_period: form.getValues('reporting_period'),
  second_label_line_value_type: form.getValues('second_label_line_value_type'),
  show_canvas_resolution: form.getValues('show_canvas_resolution'),
  show_comparison_line: form.getValues('show_comparison_line'),
  show_data_editor: form.getValues('show_data_editor'),
  show_imbalances: form.getValues('show_imbalances'),
  show_labels: form.getValues('show_labels'),
  show_title: form.getValues('show_title'),
  show_watermark: form.getValues('show_watermark'),
  statement_type: form.getValues('statement_type'),
  team_permission: form.getValues('team_permission'),
  title_font_color: form.getValues('title_font_color'),
  title_font_size: form.getValues('title_font_size'),
  unit_prefix: form.getValues('unit_prefix'),
  unit_suffix: form.getValues('unit_suffix'),
  use_custom_title: form.getValues('use_custom_title'),
  /* Overrides */
  js_input: convertToMaticInput(
    sankeyFormFlowsMapToArray(form.getValues('flows')),
    sankeyFormNodesMapToArray(form.getValues('nodes'))
  ),
  chart: getChartFromSankeyFormValues(form.getValues()),
  // Pass the SVG representation to the backend without the <defs> tag
  svg_representation: getBareSVGRepresentation(),
});

/*
 * Converts Sankey model to the form data.
 */
export const sankeyToForm = (sankey: Sankey): SankeyFormValues => {
  const parsed = new JSInputParser(sankey.js_input ?? '').parse(
    (sourceLine) => {
      analytics.reportError('Diagram parsing error', { sourceLine }, false);
    }
  );
  const formValues = {
    ...sankey,
    nodes: sankeyFormNodesArrayToMap(parsed.nodes),
    flows: sankeyFormFlowsArrayToMap(
      parsed.flows.map((flow) => ({
        ...flow,
        source: flow.source.name,
        target: flow.target.name,
      }))
    ),
    // Use fallback values for custom canvas width and height to make sure we always have valid sizes defined
    canvas_width: sankey.canvas_width || 960,
    canvas_height: sankey.canvas_height || 600,
  };
  // Return form values with populated chart JSON field
  return { ...formValues, chart: getChartFromSankeyFormValues(formValues) };
};

export const emptySankeyFormValues = sankeyToForm(emptySankey);
export const emptySankeyFormState = JSON.stringify(emptySankeyFormValues);

/**
 * Hook that propagates changes from a field in a draft form to the corresponding field
 * in the upstream form. This is useful for nested forms where changes need to be synced
 * back to parent autosave-tied form after the draft form is made sure to be valid.
 *
 * @param field - Path to the form field that should be propagated to the upstream form
 * @param delayMs - Delay in milliseconds before propagating the value (for debouncing)
 */
export const useUpstreamFormPropagation = (
  field: SankeyFormPath,
  delayMs: number
) => {
  const draftForm = useFormContext<SankeyFormValues>();
  const { upstreamForm } = useUpstreamFormContext();
  const enabled = !!upstreamForm;

  // Debounced function to propagate the value from the
  // draft form to the upstream form if the field is valid.
  const debouncedPropagateToOriginalForm = useMemo(
    () =>
      debounce(() => {
        draftForm.trigger(field).then((isValid) => {
          if (isValid && upstreamForm) {
            upstreamForm.setValue(field, draftForm.getValues(field), {
              shouldDirty: true,
            });
            updateChart(upstreamForm);
          }
        });
      }, delayMs),
    [field, delayMs]
  );

  useEffect(() => {
    // Propagate values from the draft form to the upstream form if it exists
    if (enabled) {
      draftForm.register(field, {
        onChange: () => debouncedPropagateToOriginalForm(),
      });
    }
    // Cleanup function to unregister the fields and cancel the debounced
    // function when the component unmounts or the field changes.
    return () => {
      if (enabled) {
        draftForm.unregister(field);
      }
      debouncedPropagateToOriginalForm.cancel();
    };
  }, [field, enabled, debouncedPropagateToOriginalForm]);
};
